import {
  AttachFile,
  AutoAwesome,
  CloudUpload,
  DeleteOutline,
  Inbox,
  MailOutline,
  Outbox,
  PrintOutlined,
  Reply,
  Schedule,
  Search,
  ForwardToInbox,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useCommunicationByLegalClient,
  useCommunicationByLegalTransaction,
  useDeleteCommunicationItem,
  useReplyToIncoming,
} from '../hooks/reactQuerys/CommunicationQueries';
import {
  ICommunicationItem,
  IPendingReply,
} from '../Interfaces/ICommunication';
import { CustomEditor } from '../core/CustomEditor';
import {
  useGetKiStatus,
  useKiAntwortEntwurf,
} from '../hooks/reactQuerys/KiQueries';
import {
  RecordIncomingDialog,
  RecordIncomingPrefill,
} from './RecordIncomingDialog';
import { isParseableEmail, parseEmailFile } from './parseEmailFile';
import { buildEmailPrintHtml } from './emailPrint';
import {
  useGetUsers,
  useGetUserGroups,
} from '../hooks/reactQuerys/UserQueries';

interface IProps {
  /** Genau eines von beiden setzen — die Komponente zeigt die Kommunikation
   *  zum jeweiligen Mandanten oder Vorgang. */
  idLegalClient?: number | null;
  idLegalTransaction?: number | null;
  /** Wenn gesetzt, wird der Reply-Entwurf nach oben gereicht statt sofort
   *  gesendet. Ist dieser Prop nicht vorhanden, wird sofort gesendet. */
  onPendingReply?: (pending: IPendingReply | null) => void;
  /** Gibt an, ob noch eine ungesendete Antwort aussteht (vom Parent gesteuert). */
  hasPendingReply?: boolean;
  /** Vorgangs-Metadaten für den E-Mail-Druck (nur im Vorgangs-Kontext gesetzt).
   *  IDs werden hier zu Namen aufgelöst. */
  printContext?: {
    title?: string | null;
    /** interne DiNo-Vorgangsnummer (idLegalTransaction) */
    dinoNumber?: number | null;
    fileNumbers?: string[];
    recordNumbers?: string[];
    notaryIdUser?: number | null;
    employeeIdUser?: number | null;
    userGroupId?: number | null;
  };
}

// Date-Formatter im Thunderbird-Stil:
//   heute        → HH:MM
//   diese Woche  → „Mi 14:32"
//   dieses Jahr  → „12. Mai"
//   sonst        → „12.05.2026"
const compactDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const sameYear = d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  const diffDays = (now.getTime() - d.getTime()) / 86400000;
  if (sameYear && diffDays < 7) {
    return (
      d.toLocaleDateString('de-DE', { weekday: 'short' }) +
      ' ' +
      d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    );
  }
  if (sameYear) {
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  }
  return d.toLocaleDateString('de-DE');
};

const fullDate = (iso?: string): string => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
};

const stripHtml = (html: string): string =>
  html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const previewText = (item: ICommunicationItem): string => {
  const raw = item.Body ?? '';
  const cleaned =
    item.BodyContentType === 'html'
      ? stripHtml(raw)
      : raw.replace(/\s+/g, ' ').trim();
  return cleaned.slice(0, 180);
};

// „<Display Name> <addr@host>" zerlegen.
const partyDisplay = (
  raw?: string | null
): { name: string; address: string } => {
  if (!raw) return { name: '', address: '' };
  const m = String(raw).match(/^\s*(.*?)\s*<([^<>]+)>\s*$/);
  if (m) {
    return {
      name: (m[1] || '').trim().replace(/^"|"$/g, ''),
      address: (m[2] || '').trim(),
    };
  }
  return { name: '', address: String(raw).trim() };
};

const initialsFor = (display: { name: string; address: string }): string => {
  const src = display.name || display.address;
  if (!src) return '?';
  const parts = src.split(/[\s.@]+/).filter(Boolean);
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (a + b).toUpperCase() || src[0].toUpperCase();
};

// Stabile Farbe pro Korrespondent (HSL-Hash) — wiederkehrende Adressen sind
// dadurch in der Liste leicht wiederzuerkennen.
const colorFor = (key: string): string => {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(h) % 360}, 55%, 45%)`;
};

export const CommunicationTab: React.FC<IProps> = ({
  idLegalClient,
  idLegalTransaction,
  onPendingReply,
  hasPendingReply,
  printContext,
}) => {
  const fromClient = useCommunicationByLegalClient(idLegalClient ?? null);
  const fromTransaction = useCommunicationByLegalTransaction(
    idLegalTransaction ?? null
  );
  const items = idLegalClient ? fromClient.items : fromTransaction.items;
  const isLoading = idLegalClient
    ? fromClient.isLoading
    : fromTransaction.isLoading;
  const refetch = idLegalClient ? fromClient.refetch : fromTransaction.refetch;

  const [search, setSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState<
    'all' | 'incoming' | 'outgoing' | 'planned'
  >('all');
  const [showRecord, setShowRecord] = useState(false);
  const [recordPrefill, setRecordPrefill] = useState<
    RecordIncomingPrefill | undefined
  >(undefined);
  const [selected, setSelected] = useState<ICommunicationItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ICommunicationItem | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { deleteCommunicationItem, isDeletingCommunicationItem } =
    useDeleteCommunicationItem();
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [showReplySentInfo, setShowReplySentInfo] = useState(false);
  const { replyToIncoming, isReplying } = useReplyToIncoming();

  //////// Antwortentwurf durch die KI (#1336)
  const { kiStatus } = useGetKiStatus();
  const { entwerfeAntwort, isPendingEntwerfeAntwort } = useKiAntwortEntwurf();
  /** Was vor dem Absenden noch zu klären ist — steht über den Schaltflächen. */
  const [kiOffenePunkte, setKiOffenePunkte] = useState<string[]>([]);
  const [kiError, setKiError] = useState<string | null>(null);

  async function handleKiEntwurf() {
    if (!selected?.idIncomingEmail) return;

    // Ein Entwurf, der sich in bereits Geschriebenes hineinschiebt, wäre
    // schlimmer als keiner: man merkt es erst beim Lesen des Ergebnisses.
    if (
      stripHtml(replyBody).trim() !== '' &&
      !window.confirm(
        'Der Entwurf ersetzt den bereits geschriebenen Text. Fortfahren?'
      )
    ) {
      return;
    }

    setKiError(null);
    setKiOffenePunkte([]);

    try {
      const entwurf = await entwerfeAntwort({
        idIncomingEmail: selected.idIncomingEmail,
      });

      // Der Editor arbeitet mit HTML, der Entwurf kommt als Text.
      setReplyBody(
        entwurf.Body.split(/\n{2,}/)
          .map(absatz => `<p>${absatz.replace(/\n/g, '<br>')}</p>`)
          .join('')
      );

      if (entwurf.Subject !== null) {
        setReplySubject(entwurf.Subject);
      }

      const punkte = [...entwurf.OffenePunkte];

      if (!entwurf.HatVorgangskontext) {
        // Ohne Sachstand kann der Entwurf nur den Eingang bestätigen. Wer das
        // nicht weiß, hält ihn für inhaltlich geprüft.
        punkte.push(
          'Diese E-Mail hängt an keinem Vorgang — der Entwurf kennt den Sachstand nicht.'
        );
      }

      setKiOffenePunkte(punkte);
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setKiError(
        detail !== '' ? detail : 'Es konnte kein Entwurf erzeugt werden.'
      );
    }
  }

  // Für den E-Mail-Druck: Namen zu den IDs aus printContext sowie den aktuell
  // angemeldeten Benutzer ("Gedruckt von") auflösen.
  const { users } = useGetUsers();
  const { userGroups } = useGetUserGroups();
  const resolveUserName = useCallback(
    (id?: number | null): string => {
      if (!id) return '';
      const u = (users ?? []).find(x => x.idUser === id);
      return u ? `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim() : '';
    },
    [users]
  );
  const resolveGroupName = useCallback(
    (id?: number | null): string => {
      if (!id) return '';
      return (
        (userGroups ?? []).find(g => g.idUserGroup === id)?.UserGroup ?? ''
      );
    },
    [userGroups]
  );
  const currentUserName = useMemo(
    () => resolveUserName(Number(localStorage.getItem('idUser')) || null),
    [resolveUserName]
  );

  const handlePrint = useCallback(
    (item: ICommunicationItem) => {
      const meta = printContext
        ? {
            title: printContext.title,
            dinoNumber: printContext.dinoNumber,
            fileNumbers: printContext.fileNumbers,
            recordNumbers: printContext.recordNumbers,
            notaryName: resolveUserName(printContext.notaryIdUser),
            employeeOrGroup:
              resolveUserName(printContext.employeeIdUser) ||
              resolveGroupName(printContext.userGroupId),
          }
        : undefined;
      const html = buildEmailPrintHtml(
        item,
        meta,
        currentUserName,
        new Date().toISOString()
      );
      const win = window.open('', '_blank', 'width=900,height=700');
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      const triggerPrint = () => {
        try {
          win.focus();
          win.print();
        } catch {
          /* Druckdialog vom Browser blockiert — Nutzer kann manuell drucken. */
        }
      };
      // onload greift bei Bildern/Fonts; Timeout als Fallback.
      win.onload = triggerPrint;
      window.setTimeout(triggerPrint, 600);
    },
    [printContext, resolveUserName, resolveGroupName, currentUserName]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete?.id) return;
    setDeleteError(null);
    try {
      await deleteCommunicationItem(pendingDelete.id);
      if (selected?.id === pendingDelete.id) {
        setSelected(null);
      }
      setPendingDelete(null);
      void refetch();
    } catch (e) {
      setDeleteError(
        e instanceof Error
          ? `Löschen fehlgeschlagen: ${e.message}`
          : 'Löschen fehlgeschlagen.'
      );
    }
  }, [deleteCommunicationItem, pendingDelete, refetch, selected]);
  const [dragActive, setDragActive] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const [isParsingDrop, setIsParsingDrop] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Window-weiter Guard: verhindert, dass der Browser eine fallengelassene
  // Datei oeffnet/herunterlaedt, wenn der Drop knapp NEBEN die Dropzone faellt
  // (z. B. auf den Hinweis-Text darueber). Ohne diesen Guard navigiert Firefox
  // zur Datei und zeigt den „Datei oeffnen/speichern"-Dialog statt zu
  // importieren (#1069). ``preventDefault`` unterbindet nur das Browser-
  // Default — die React-``onDrop``-Handler der eigentlichen Dropzone feuern
  // weiterhin, der Import bleibt also funktionsfaehig.
  useEffect(() => {
    const block = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, []);

  const handleParsedFile = useCallback(async (file: File) => {
    setIsParsingDrop(true);
    setDropError(null);
    try {
      const parsed = await parseEmailFile(file);
      setRecordPrefill({
        from: parsed.from,
        to: parsed.to,
        cc: parsed.cc,
        subject: parsed.subject,
        body: parsed.body,
        bodyContentType: parsed.bodyContentType,
        receivedAt: parsed.receivedAt,
        externalMessageId: parsed.externalMessageId,
        attachments: parsed.attachments.map(a => ({
          fileName: a.fileName,
          data: a.data,
        })),
        infoMessage: `E-Mail aus "${file.name}" (${parsed.format.toUpperCase()}) geladen.${
          parsed.warning ? `\nHinweis: ${parsed.warning}` : ''
        }${
          parsed.attachments.length > 0
            ? `\n${parsed.attachments.length} Anhang/Anhaenge uebernommen.`
            : ''
        }\nBitte oben die Richtung (eingehend/ausgehend) waehlen und anschliessend speichern.`,
      });
      setShowRecord(true);
    } catch (e) {
      setDropError(
        e instanceof Error
          ? `E-Mail konnte nicht gelesen werden: ${e.message}`
          : 'E-Mail konnte nicht gelesen werden.'
      );
    } finally {
      setIsParsingDrop(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragActive(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length === 0) {
        setDropError(
          'Aus dem geoeffneten Mailprogramm kann der Browser keine Datei lesen. ' +
            'Bitte die Mail zuerst per "Speichern unter" als .eml oder .msg auf den Rechner ablegen ' +
            'und diese Datei in den Tab ziehen.'
        );
        return;
      }
      const supported = files.find(isParseableEmail) ?? files[0];
      if (!isParseableEmail(supported)) {
        setDropError(
          `Datei "${supported.name}" wird nicht unterstuetzt — bitte .eml oder .msg verwenden.`
        );
        return;
      }
      void handleParsedFile(supported);
    },
    [handleParsedFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Wert zuruecksetzen, damit dieselbe Datei erneut waehlbar ist.
      e.target.value = '';
      if (!file) return;
      if (!isParseableEmail(file)) {
        setDropError(
          `Datei "${file.name}" wird nicht unterstuetzt — bitte .eml oder .msg verwenden.`
        );
        return;
      }
      void handleParsedFile(file);
    },
    [handleParsedFile]
  );

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    // Nur reagieren, wenn der Drag tatsaechlich Dateien enthaelt — sonst
    // flackert die Zone bei jedem internen Drag (z. B. Text markieren).
    if (e.dataTransfer?.types?.includes('Files')) {
      setDragActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchesDirection = (it: ICommunicationItem): boolean => {
      switch (directionFilter) {
        case 'incoming':
          return it.Direction === 'incoming';
        case 'outgoing':
          return it.Direction === 'outgoing' && it.Status === 'sent';
        case 'planned':
          return it.Direction === 'outgoing' && it.Status !== 'sent';
        default:
          return true;
      }
    };
    return items.filter(it => {
      if (!matchesDirection(it)) return false;
      if (!q) return true;
      return [it.Subject, it.From, it.To, it.Counterparty, it.Body]
        .filter(Boolean)
        .map(s => String(s).toLowerCase())
        .some(s => s.includes(q));
    });
  }, [items, search, directionFilter]);

  return (
    <Box
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      sx={{ position: 'relative' }}
    >
      {/* Drop-Overlay deckt den GESAMTEN Tab-Bereich ab — egal wo man die
          Datei loslaesst, der Import wird ausgeloest (#1069). */}
      {(dragActive || isParsingDrop) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            bgcolor: 'rgba(25, 118, 210, 0.10)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {isParsingDrop ? (
            <>
              <CircularProgress size={36} />
              <Typography
                variant='subtitle1'
                sx={{ mt: 2, color: 'primary.main' }}
              >
                E-Mail wird gelesen…
              </Typography>
            </>
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 56, color: 'primary.main' }} />
              <Typography variant='h6' sx={{ mt: 1, color: 'primary.main' }}>
                E-Mail hier ablegen
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                .eml- oder .msg-Datei — einfach irgendwo in diesem Bereich
                loslassen. Absender, Empfänger, Betreff, Inhalt und Anhänge
                werden übernommen.
              </Typography>
            </>
          )}
        </Box>
      )}
      <Alert
        severity='info'
        icon={<CloudUpload fontSize='small' />}
        sx={{ m: 1, mt: 1, '& .MuiAlert-message': { fontSize: 12 } }}
      >
        <strong>E-Mail per Drag&amp;Drop hinzufügen:</strong>
        <br />
        <strong>Thunderbird:</strong> Mail markieren → mit gedrückter Maustaste
        auf den Desktop ziehen (Thunderbird erzeugt eine .eml-Datei) → diese
        Datei in den Tab hier ziehen.
        <br />
        <strong>Outlook:</strong> Mail per „Datei → Speichern unter" als .msg
        ablegen oder in einen Ordner ziehen → Datei in den Tab hier ziehen.
        <br />
        <Box sx={{ mt: 1 }}>
          <Button
            size='small'
            variant='outlined'
            startIcon={<CloudUpload fontSize='small' />}
            onClick={() => fileInputRef.current?.click()}
          >
            Datei auswählen
          </Button>
          <input
            ref={fileInputRef}
            type='file'
            accept='.eml,.msg'
            hidden
            onChange={handleFileSelect}
          />
        </Box>
      </Alert>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          minHeight: 480,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'visible',
        }}
      >
        {/* === LISTE === */}
        <Box
          sx={{
            width: 420,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
        >
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            <TextField
              size='small'
              fullWidth
              placeholder='Suchen…'
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size='small'
              value={directionFilter}
              onChange={e =>
                setDirectionFilter(
                  e.target.value as 'all' | 'incoming' | 'outgoing' | 'planned'
                )
              }
              sx={{ minWidth: 140, flexShrink: 0 }}
            >
              <MenuItem value='all'>Alle Mails</MenuItem>
              <MenuItem value='incoming'>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Inbox fontSize='small' color='success' />
                  <span>Eingehend</span>
                </Stack>
              </MenuItem>
              <MenuItem value='outgoing'>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Outbox fontSize='small' color='primary' />
                  <span>Ausgehend</span>
                </Stack>
              </MenuItem>
              <MenuItem value='planned'>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Schedule fontSize='small' color='warning' />
                  <span>Geplant</span>
                </Stack>
              </MenuItem>
            </TextField>
            <Tooltip title='Eingang dokumentieren'>
              <Button
                variant='contained'
                size='small'
                onClick={() => {
                  setRecordPrefill(undefined);
                  setShowRecord(true);
                }}
                sx={{ flexShrink: 0, minWidth: 0, px: 1.25 }}
              >
                <ForwardToInbox fontSize='small' />
              </Button>
            </Tooltip>
          </Stack>

          {dropError && (
            <Alert
              severity='warning'
              onClose={() => setDropError(null)}
              sx={{ m: 1, mt: 0 }}
            >
              {dropError}
            </Alert>
          )}

          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <Alert severity='info' sx={{ m: 2 }}>
              Keine E-Mails. Eingehende E-Mails können über „Eingang
              dokumentieren" oder per Outlook-/Thunderbird-Plugin erfasst
              werden.
            </Alert>
          ) : (
            <List dense disablePadding>
              {filtered.map(it => (
                <MailListItem
                  key={
                    it.id ??
                    `${it.Source}-${it.idProcessManager ?? it.idIncomingEmail}`
                  }
                  item={it}
                  isSelected={selected?.id === it.id}
                  onClick={() => setSelected(it)}
                  onDelete={() => setPendingDelete(it)}
                />
              ))}
            </List>
          )}
        </Box>

        {/* === LESEPANE === */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          {selected ? (
            <ReadingPane
              item={selected}
              onPrint={() => handlePrint(selected)}
              onDelete={() => setPendingDelete(selected)}
              onReply={
                selected.Direction === 'incoming' &&
                selected.Source === 'IncomingEmail' &&
                selected.idIncomingEmail
                  ? () => {
                      const origSubject = selected.Subject || '';
                      setReplySubject(
                        origSubject.toLowerCase().startsWith('re:')
                          ? origSubject
                          : `Re: ${origSubject}`
                      );
                      setReplyBody('');
                      setReplyError(null);
                      setIsReplyOpen(true);
                    }
                  : undefined
              }
            />
          ) : (
            <Stack
              alignItems='center'
              spacing={1}
              sx={{ py: 8, color: 'text.secondary' }}
            >
              <MailOutline sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant='body2'>
                Wählen Sie eine E-Mail aus der Liste aus.
              </Typography>
            </Stack>
          )}
        </Box>

        {showRecord && (
          <RecordIncomingDialog
            idLegalClient={idLegalClient ?? null}
            idLegalTransaction={idLegalTransaction ?? null}
            prefill={recordPrefill}
            onClose={() => {
              setShowRecord(false);
              setRecordPrefill(undefined);
            }}
            onCreated={() => {
              setShowRecord(false);
              setRecordPrefill(undefined);
              void refetch();
            }}
          />
        )}
      </Box>

      <DeleteCommunicationDialog
        item={pendingDelete}
        error={deleteError}
        isDeleting={isDeletingCommunicationItem}
        onCancel={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      {/* Reply-Dialog */}
      <Dialog
        open={isReplyOpen && selected !== null}
        onClose={() => !isReplying && setIsReplyOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Antworten</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label='Betreff'
            value={replySubject}
            onChange={e => setReplySubject(e.target.value)}
            disabled={isReplying}
            sx={{ mb: 2, mt: 1 }}
          />
          <Grid container>
            <CustomEditor
              txt={replyBody}
              setTxt={setReplyBody}
              dictonaryIdsArray={[1, 2, 3]}
            />
          </Grid>
          {kiOffenePunkte.length > 0 && (
            <Alert severity='warning' sx={{ mt: 1.5 }}>
              <AlertTitle>Vor dem Absenden zu klären</AlertTitle>
              <Stack component='ul' sx={{ m: 0, pl: 2 }}>
                {kiOffenePunkte.map(punkt => (
                  <Typography component='li' variant='caption' key={punkt}>
                    {punkt}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
          {kiError !== null && (
            <Alert severity='info' sx={{ mt: 1.5 }}>
              {kiError}
            </Alert>
          )}
          {replyError && (
            <Alert severity='error' sx={{ mt: 1.5 }}>
              {replyError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {kiStatus?.Ready === true && kiStatus.HonorsSystemPrompt === true && (
            // Nur bei einem Anbieter, der sich an DiNos Auftrag hält: dort
            // stehen die Regeln für Notariatsschreiben (keine Rechtsauskunft,
            // nichts erfinden). Bei watsonx gehen die Agenten-Anweisungen vor.
            // Der Entwurf ersetzt den Text im Feld. Ein Entwurf, der sich
            // dazwischenschiebt, waere schlimmer als keiner — deshalb fragt
            // die Schaltflaeche nach, sobald schon etwas dasteht.
            <Button
              startIcon={
                isPendingEntwerfeAntwort ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesome />
                )
              }
              disabled={isReplying || isPendingEntwerfeAntwort}
              onClick={() => void handleKiEntwurf()}
              sx={{ mr: 'auto' }}
            >
              Entwurf vorschlagen
            </Button>
          )}
          <Button onClick={() => setIsReplyOpen(false)} disabled={isReplying}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            disabled={isReplying || !stripHtml(replyBody).trim()}
            startIcon={
              isReplying ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <Reply />
              )
            }
            onClick={async () => {
              if (!selected?.idIncomingEmail) return;
              setReplyError(null);
              const pendingData: IPendingReply = {
                idIncomingEmail: selected.idIncomingEmail,
                subject: replySubject.trim() || undefined,
                body: replyBody.trim(),
              };
              if (onPendingReply) {
                // Entwurf an Parent übergeben — API-Call erst beim Speichern des Vorgangs
                onPendingReply(pendingData);
                setIsReplyOpen(false);
                setReplyBody('');
                setReplySubject('');
                setShowReplySentInfo(true);
              } else {
                // Kein Pending-Flow (z.B. Mandanten-Ansicht) — sofort senden
                try {
                  await replyToIncoming({
                    idIncomingEmail: pendingData.idIncomingEmail,
                    subject: pendingData.subject,
                    body: pendingData.body,
                  });
                  setIsReplyOpen(false);
                  setReplyBody('');
                  setReplySubject('');
                  void refetch();
                  setShowReplySentInfo(true);
                } catch {
                  setReplyError(
                    'Die Antwort konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
                  );
                }
              }
            }}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info-Dialog nach dem Senden */}
      <Dialog
        open={showReplySentInfo}
        onClose={() => setShowReplySentInfo(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Antwort gespeichert</DialogTitle>
        <DialogContent>
          <Alert severity='info' icon={false}>
            {onPendingReply
              ? 'Die Antwort wird erst versendet, wenn Sie den Vorgang speichern. Bitte speichern Sie den Vorgang, um die E-Mail abzuschicken.'
              : 'Ihre Antwort wurde übermittelt und wird in Kürze versendet.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            onClick={() => setShowReplySentInfo(false)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Lösch-Dialog mit kontextabhängigem Warntext:
//   • Geplant   → harmlos, nur Hinweis, dass die Mail nicht mehr rausgeht
//   • Eingang   → loescht den Eintrag inkl. Anhaengen
//   • Gesendet  → Audit-Trail-Hinweis (Mail ist schon raus, hier wird nur
//                 die Historie entfernt)
// ---------------------------------------------------------------------------
interface DeleteDialogProps {
  item: ICommunicationItem | null;
  error: string | null;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteCommunicationDialog: React.FC<DeleteDialogProps> = ({
  item,
  error,
  isDeleting,
  onCancel,
  onConfirm,
}) => {
  if (!item) return null;
  const isOutgoing = item.Direction === 'outgoing';
  const isPlanned = isOutgoing && item.Status !== 'sent';
  const isSent = isOutgoing && item.Status === 'sent';

  let title = 'E-Mail-Eintrag löschen?';
  let body: React.ReactNode = 'Diesen E-Mail-Eintrag wirklich löschen?';
  let severity: 'info' | 'warning' = 'info';
  if (isPlanned) {
    title = 'Geplante E-Mail abbrechen?';
    body = (
      <>
        Die geplante E-Mail mit dem Betreff{' '}
        <strong>„{item.Subject || '(ohne Betreff)'}"</strong>
        &nbsp;wird gelöscht und nicht mehr versendet.
      </>
    );
  } else if (isSent) {
    title = 'Bereits versendete E-Mail aus der Historie entfernen?';
    body = (
      <>
        Diese E-Mail wurde bereits versendet. Beim Löschen verschwindet nur der
        Eintrag aus der Vorgangs-Historie — beim Empfänger bleibt die Mail
        bestehen.
      </>
    );
    severity = 'warning';
  } else {
    title = 'Eingehende E-Mail löschen?';
    body = (
      <>
        Der Eintrag <strong>„{item.Subject || '(ohne Betreff)'}"</strong> und
        alle zugehörigen Anhänge werden gelöscht.
      </>
    );
    severity = 'warning';
  }

  return (
    <Dialog
      open
      onClose={isDeleting ? undefined : onCancel}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component='div'>{body}</DialogContentText>
        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isDeleting}>
          Abbrechen
        </Button>
        <Button
          color={severity === 'warning' ? 'error' : 'primary'}
          variant='contained'
          onClick={onConfirm}
          disabled={isDeleting}
          startIcon={
            isDeleting ? <CircularProgress size={16} /> : <DeleteOutline />
          }
        >
          {isDeleting ? 'Lösche…' : 'Löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// MailListItem — eine Zeile in der Mail-Liste, ein-/ausgehend visuell
// klar unterschieden:
//   • farbiger 4px-Linker Rand (grün eingehend / blau ausgehend)
//   • Avatar des Korrespondenten mit Direction-Badge (Inbox/Outbox)
//   • bei „Gesendet" der Empfänger prominent (sonst der Absender)
// ---------------------------------------------------------------------------

interface MailListItemProps {
  item: ICommunicationItem;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
}

const MailListItem: React.FC<MailListItemProps> = ({
  item,
  isSelected,
  onClick,
  onDelete,
}) => {
  const isOutgoing = item.Direction === 'outgoing';
  // Geplante (noch nicht versendete) Mails optisch klar von tatsächlich
  // versendeten trennen: Akzent in „warning"-Orange, Direction-Badge mit
  // Uhren-Icon, Präfix „Geplant an:" statt „An:".
  const isPlanned = isOutgoing && item.Status !== 'sent';
  const partyRaw = isOutgoing
    ? item.To || item.Counterparty
    : item.From || item.Counterparty;
  const party = partyDisplay(partyRaw);
  const partyLabel = party.name || party.address || '–';
  const partyKey = party.address || party.name || '?';

  const accent = isPlanned
    ? 'warning.main'
    : isOutgoing
      ? 'primary.main'
      : 'success.main';
  const prefix = isOutgoing ? (isPlanned ? 'Geplant an:' : 'An:') : 'Von:';

  return (
    <ListItem
      disablePadding
      sx={{
        position: 'relative',
        '&:hover .mailRowDelete': { opacity: 1 },
      }}
    >
      {/* Farbiger Linker Rand für Direction-Erkennung auf einen Blick */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        }}
      />
      <ListItemButton
        selected={isSelected}
        onClick={onClick}
        sx={{
          alignItems: 'flex-start',
          py: 1,
          pl: 2,
          pr: 1.5,
          gap: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Badge
          overlap='circular'
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Box
              sx={{
                bgcolor: accent,
                color: '#fff',
                borderRadius: '50%',
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 2,
                borderColor: 'background.paper',
              }}
            >
              {isPlanned ? (
                <Schedule sx={{ fontSize: 10 }} />
              ) : isOutgoing ? (
                <Outbox sx={{ fontSize: 10 }} />
              ) : (
                <Inbox sx={{ fontSize: 10 }} />
              )}
            </Box>
          }
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: colorFor(partyKey),
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {initialsFor(party)}
          </Avatar>
        </Badge>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction='row' alignItems='center' spacing={0.75}>
            <Typography
              variant='body2'
              fontWeight={600}
              noWrap
              sx={{ flex: 1 }}
              title={partyRaw ?? ''}
            >
              <Typography
                component='span'
                variant='caption'
                sx={{
                  mr: 0.5,
                  fontWeight: 700,
                  color: accent,
                }}
              >
                {prefix}
              </Typography>
              {partyLabel}
            </Typography>
            {(item.Attachments?.length ?? 0) > 0 && (
              <AttachFile fontSize='inherit' sx={{ color: 'text.secondary' }} />
            )}
            <Tooltip title={fullDate(item.When)}>
              <Typography variant='caption' color='text.secondary' noWrap>
                {compactDate(item.When)}
              </Typography>
            </Tooltip>
          </Stack>
          <Typography variant='body2' noWrap>
            {item.Subject || '(ohne Betreff)'}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {previewText(item)}
          </Typography>
        </Box>
      </ListItemButton>
      {/* Loeschen direkt aus der Liste — als Sibling der ListItemButton
          (kein Button-in-Button) rechts ueberlagert; dezent sichtbar, bei
          Hover voll. Klick stoppt die Propagation, damit die Zeile nicht
          zusaetzlich selektiert wird. */}
      <Tooltip title='E-Mail-Eintrag löschen'>
        <IconButton
          className='mailRowDelete'
          size='small'
          color='error'
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0.35,
            transition: 'opacity 0.15s',
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <DeleteOutline fontSize='small' />
        </IconButton>
      </Tooltip>
    </ListItem>
  );
};

// ---------------------------------------------------------------------------
// Lesepane — Thunderbird-Stil mit Header-Block + Body + Anhang-Leiste.
// ---------------------------------------------------------------------------

const ReadingPane: React.FC<{
  item: ICommunicationItem;
  onDelete?: () => void;
  onReply?: () => void;
  onPrint?: () => void;
}> = ({ item, onDelete, onReply, onPrint }) => {
  const isOutgoing = item.Direction === 'outgoing';
  const isPlanned = isOutgoing && item.Status !== 'sent';
  const fromParty = partyDisplay(item.From);
  const toParty = partyDisplay(item.To || item.Counterparty);
  const idParty = isOutgoing ? toParty : fromParty;
  const idKey = idParty.address || idParty.name || '?';
  const accentColor = isPlanned
    ? 'warning.main'
    : isOutgoing
      ? 'primary.main'
      : 'success.main';

  return (
    <>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          borderLeft: 4,
          borderLeftColor: accentColor,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Avatar
          sx={{
            bgcolor: colorFor(idKey),
            width: 48,
            height: 48,
            fontSize: 18,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initialsFor(idParty)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant='h6'
              fontWeight={600}
              sx={{ flex: 1, minWidth: 0 }}
              noWrap
              title={item.Subject || ''}
            >
              {item.Subject || '(ohne Betreff)'}
            </Typography>
            <Chip
              size='small'
              label={
                isPlanned ? 'Geplant' : isOutgoing ? 'Gesendet' : 'Eingang'
              }
              color={isPlanned ? 'warning' : isOutgoing ? 'primary' : 'success'}
              variant='outlined'
              icon={
                isPlanned ? (
                  <Schedule fontSize='small' />
                ) : isOutgoing ? (
                  <Outbox fontSize='small' />
                ) : (
                  <Inbox fontSize='small' />
                )
              }
            />
            {onReply && (
              <Tooltip title='Auf diese Nachricht antworten'>
                <IconButton size='small' color='primary' onClick={onReply}>
                  <Reply fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {onPrint && (
              <Tooltip title='E-Mail drucken'>
                <IconButton size='small' onClick={onPrint}>
                  <PrintOutlined fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip
                title={
                  isPlanned
                    ? 'Geplante E-Mail löschen (wird nicht mehr versendet)'
                    : 'E-Mail-Eintrag aus dem Vorgang löschen'
                }
              >
                <IconButton size='small' color='error' onClick={onDelete}>
                  <DeleteOutline fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <HeaderTable item={item} />
        </Box>
      </Box>

      {item.Notes && (
        <Alert severity='info' sx={{ m: 2, mb: 0 }} icon={false}>
          <Typography variant='caption' fontWeight={600} sx={{ mr: 1 }}>
            Notiz:
          </Typography>
          <Typography variant='caption'>{item.Notes}</Typography>
        </Alert>
      )}

      <Box sx={{ p: 2 }}>
        {item.BodyContentType === 'html' ? (
          // Fremd-HTML (z. B. GitLab-Mails) bringt eigene <style>-Regeln mit,
          // die sonst auf die DiNo-UI durchschlagen. Eigener Dokument-Scope
          // via iframe sandbox-isoliert den Inhalt komplett.
          <EmailHtmlFrame html={item.Body ?? ''} />
        ) : (
          <Typography
            variant='body2'
            component='pre'
            sx={{
              whiteSpace: 'pre-wrap',
              overflow: 'visible',
              m: 0,
              fontFamily: 'inherit',
              fontSize: 14,
              bgcolor: 'background.paper',
              color: 'text.primary',
            }}
          >
            {item.Body || ''}
          </Typography>
        )}
      </Box>

      {(item.Attachments?.length ?? 0) > 0 && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            p: 1.5,
            bgcolor: 'background.default',
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
            <AttachFile fontSize='small' />
            <Typography variant='subtitle2'>
              {item.Attachments?.length} Anhang
              {(item.Attachments?.length ?? 0) === 1 ? '' : 'e'}
            </Typography>
          </Stack>
          <Stack direction='row' flexWrap='wrap' gap={1}>
            {item.Attachments?.map((a, i) => (
              <Chip
                key={`${a.idDocument ?? i}`}
                icon={<AttachFile />}
                label={a.FileName || `Anhang ${i + 1}`}
                variant='outlined'
              />
            ))}
          </Stack>
        </Box>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// EmailHtmlFrame — rendert fremden HTML-Body in einer iframe-Sandbox, damit
// dessen <style>-/CSS-Regeln nicht in die umgebende DiNo-UI durchschlagen.
// Hoehe wird nach dem Laden auf die tatsaechliche scrollHeight gesetzt.
// ---------------------------------------------------------------------------
const EmailHtmlFrame: React.FC<{ html: string }> = ({ html }) => {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number>(200);

  // Wrapper: <base target="_blank"> sorgt dafuer, dass Links extern aufgehen.
  // Reset-Styles geben dem Mail-CSS einen sauberen Ausgangspunkt.
  const srcDoc = useMemo(() => {
    const reset = `
      html, body { margin: 0; padding: 0; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
          Helvetica, Arial, sans-serif;
        font-size: 14px;
        line-height: 1.55;
        color: #1a1a1a;
        word-break: break-word;
      }
      img { max-width: 100%; height: auto; }
      a { color: #1976d2; }
      blockquote {
        border-left: 3px solid #ddd;
        margin: 0;
        padding-left: 12px;
        color: #666;
      }
      pre {
        background: #f5f5f5;
        padding: 8px;
        border-radius: 4px;
        overflow-x: auto;
      }
    `;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>${reset}</style></head><body>${html}</body></html>`;
  }, [html]);

  const resize = useCallback(() => {
    const iframe = ref.current;
    if (!iframe || !iframe.contentDocument) return;
    const body = iframe.contentDocument.body;
    const docEl = iframe.contentDocument.documentElement;
    const h = Math.max(
      body?.scrollHeight ?? 0,
      body?.offsetHeight ?? 0,
      docEl?.scrollHeight ?? 0
    );
    if (h > 0 && h !== height) setHeight(h);
  }, [height]);

  // Nach dem ersten Render-Zyklus nochmal nachmessen (Bilder/Fonts brauchen
  // einen Tick laenger als das Load-Event).
  useEffect(() => {
    const t1 = window.setTimeout(resize, 50);
    const t2 = window.setTimeout(resize, 500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [srcDoc, resize]);

  return (
    <iframe
      ref={ref}
      title='E-Mail-Inhalt'
      srcDoc={srcDoc}
      // allow-same-origin + allow-popups: Links extern, kein JS aus der Mail.
      sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
      onLoad={resize}
      style={{
        width: '100%',
        height,
        border: 'none',
        display: 'block',
      }}
    />
  );
};

const HeaderRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack direction='row' spacing={1} alignItems='baseline'>
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{ minWidth: 56, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant='caption'
      sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
    >
      {value}
    </Typography>
  </Stack>
);

const HeaderTable: React.FC<{ item: ICommunicationItem }> = ({ item }) => (
  <Stack spacing={0.25}>
    {item.From && <HeaderRow label='Von' value={item.From} />}
    {item.To && <HeaderRow label='An' value={item.To} />}
    {item.Cc && <HeaderRow label='Cc' value={item.Cc} />}
    {item.When && (
      <HeaderRow
        label='Datum'
        value={
          <Tooltip title={fullDate(item.When)}>
            <span>{fullDate(item.When)}</span>
          </Tooltip>
        }
      />
    )}
  </Stack>
);
